/**
 * Aspect — Plan editor ↔ scene sync layer (no DOM, no SVG)
 * --------------------------------------------------------------
 * Non-visual sync logic extracted from plan_editor.js: pushing plan-space
 * furniture poses into the live 3D scene / over the wire, mirroring scene
 * placements back into the plan model, and applying floor geometry data to the
 * room record. The SVG editor calls these; none of them render anything.
 */

import { normalizeYaw } from "../scene/transforms.js";
import { isWallMounted } from "../scene/furniture_placement.js";
import { pxToWorld, worldToPx } from "./plan_mapping.js";

/** Keep wall / ceiling / stacked height when the 2D planner only edits XZ. */
export function planFurnitureY(rec, scene, meta = null) {
  const floorY = scene?.room?.floorY ?? 0;
  const ceilH = Number(scene?.room?.dimensions?.height) || 2.7;
  const mode = rec?.placement_mode ?? meta?.placement_mode ?? null;
  const prevY = Number(rec?.position?.[1]);
  const elevated = Number.isFinite(prevY) && prevY > floorY + 0.2;
  if (elevated || rec?.parent_id) return Number.isFinite(prevY) ? prevY : floorY;
  if (mode === "ceiling") return floorY + ceilH - 0.04;
  if (mode === "wall" || isWallMounted(rec) || isWallMounted(meta)) return floorY + 1.29;
  return Number.isFinite(prevY) ? prevY : floorY;
}

/**
 * Push a furniture pose from plan px/py + rotY into the live 3D scene.
 * @returns {object|null} the transform payload for the wire, or null
 */
export function applyFurniturePoseToScene(model, scene, f) {
  if (!f?.placementId || !scene) return null;
  const rec = scene.placements.get(f.placementId);
  if (!rec) return null;
  const { x, z } = pxToWorld(model, f.px, f.py);
  const floorY = scene.room?.floorY ?? 0;
  const prev = [...(rec.position ?? [0, floorY, 0])];
  const meta = scene.catalog?.get?.(rec.asset_id) ?? scene.catalog?.get?.(f.asset_id) ?? null;
  const y = planFurnitureY(
    { ...rec, position: [prev[0], Number.isFinite(f.worldY) ? f.worldY : prev[1], prev[2]] },
    scene,
    meta,
  );
  const payload = {
    id: f.placementId,
    position: [x, y, z],
    rotation_y: normalizeYaw(f.rotY ?? 0),
    scale: rec.scale ?? 1,
    parent_id: rec.parent_id ?? null,
  };
  scene.onUpdateTransform(payload);

  // Move stacked children with the parent (same ΔXZ).
  const dx = x - prev[0];
  const dz = z - prev[2];
  if (Math.abs(dx) > 1e-6 || Math.abs(dz) > 1e-6) {
    for (const child of scene.placements.values()) {
      if (child.parent_id !== f.placementId) continue;
      const childPayload = {
        id: child.id,
        position: [child.position[0] + dx, child.position[1], child.position[2] + dz],
        rotation_y: child.rotation_y ?? 0,
        scale: child.scale ?? 1,
        parent_id: child.parent_id,
      };
      scene.onUpdateTransform(childPayload);
    }
  }
  return payload;
}

/** Notify the server of a furniture transform (plan-editor gizmo path). */
export function sendFurnitureTransform(net, payload) {
  if (!payload || !net) return;
  net.notify("viewport:transform_changed", payload);
}

/**
 * Mirror a scene placement into the plan model's furniture list (px space).
 * Updates the existing item in place or appends a new one.
 */
export function upsertFurnitureFromPlacement(model, p) {
  if (!p?.id || !model) return;
  const x = p.position?.[0];
  const z = p.position?.[2];
  if (!Number.isFinite(x) || !Number.isFinite(z)) return;
  const { px, py } = worldToPx(model, x, z);
  const existing = (model.furniture ?? []).find((f) => f.placementId === p.id);
  if (existing) {
    existing.px = px;
    existing.py = py;
    existing.rotY = p.rotation_y ?? 0;
    existing.asset_id = p.asset_id;
    if (Number.isFinite(p.position?.[1])) existing.worldY = p.position[1];
    return;
  }
  if (!model.furniture) model.furniture = [];
  model.furniture.push({
    id: model.nextFurniture++,
    placementId: p.id,
    asset_id: p.asset_id,
    px,
    py,
    rotY: p.rotation_y ?? 0,
    ...(Number.isFinite(p.position?.[1]) ? { worldY: p.position[1] } : {}),
  });
}

/**
 * Apply floor bounds + polygon from the plan onto the live room record
 * (data only — envelope/mesh refresh is the caller's geometry step).
 * @param {object} room live scene room
 * @param {{minX:number,minZ:number,maxX:number,maxZ:number,width:number,depth:number}} b floor bounds in meters
 * @param {number[][]} polygon floor polygon in meters (may be empty)
 * @param {number} fallbackHeight ceiling height when the room has none
 */
export function applyFloorDataToRoom(room, b, polygon, fallbackHeight = 2.7) {
  if (!room || !b) return;
  const height = room.dimensions?.height ?? fallbackHeight;
  room.bounds = { min: [b.minX, 0, b.minZ], max: [b.maxX, height, b.maxZ] };
  room.dimensions = { width: b.width, depth: b.depth, height };
  if (polygon?.length >= 3) {
    const primary = room.floors?.[0] ?? { id: "f_main", y: room.floorY ?? 0, polygon: [] };
    primary.polygon = polygon;
    if (!room.floors?.length) room.floors = [primary];
    else room.floors[0] = primary;
    room.floorPolygon = polygon;
  }
}
